import React, { useEffect, useState } from "react";
import api from "../../api";
import { notify } from "../../utils/toast";

export const EmployeeAttendance = ({ employeeId }) => {
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAttendance = async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const res = await api.getEmployeeAttendance(employeeId);
      const records = Array.isArray(res) ? res : [];
      setAttendanceHistory(records);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const todayAtt = records.find((att) => {
        const attDate = new Date(att.date);
        attDate.setHours(0, 0, 0, 0);
        return attDate.getTime() === today.getTime();
      });

      setTodayAttendance(todayAtt || null);
    } catch (err) {
      console.error(err);
      notify.error("Failed to fetch attendance");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendance();
  }, [employeeId]);

  const handleCheckIn = async () => {
    if (todayAttendance?.checkIn)
      return notify.error("Already checked in today!");
    setLoading(true);
    try {
      const res = await api.markAttendance({ employee: employeeId });
      setTodayAttendance(res);
      notify.success("Checked in successfully!");
      fetchAttendance();
    } catch (err) {
      notify.error(err.message || "Failed to check in");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!todayAttendance?.checkIn)
      return notify.error("No check-in found for today!");
    if (todayAttendance.checkOut)
      return notify.error("Already checked out today!");

    setLoading(true);
    try {
      const res = await api.markCheckout(todayAttendance._id, {
        checkOut: new Date(),
      });
      setTodayAttendance(res);
      notify.success("Checked out successfully!");
      fetchAttendance();
    } catch (err) {
      notify.error(err.message || "Failed to check out");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "-";
  const formatTime = (date) =>
    date
      ? new Date(date).toLocaleTimeString(undefined, {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "-";

  const getStatusBadge = (item) => {
    if (item.checkIn && item.checkOut) {
      return <span className="badge bg-success">Present</span>;
    } else if (item.checkIn && !item.checkOut) {
      return <span className="badge bg-warning text-dark">In Progress</span>;
    } else {
      return <span className="badge bg-danger">Absent</span>;
    }
  };

  return (
    <div className="DashboardMain p-3">
      <h3 className="mb-4">My Attendance</h3>

      {/* Actions */}
      <div className="AttendanceActions mb-4 d-flex gap-2">
        <button
          className="btn btn-outline-primary px-4"
          onClick={handleCheckIn}
          disabled={loading || !!todayAttendance?.checkIn}
        >
          Check In
        </button>
        <button
          className="btn btn-outline-success px-4"
          onClick={handleCheckOut}
          disabled={
            loading || !todayAttendance?.checkIn || !!todayAttendance?.checkOut
          }
        >
          Check Out
        </button>
      </div>

      {/* Attendance History */}
      <div className="TableMain mt-4">
        <div className="card shadow-sm border-0 rounded-3">
          <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center px-4 py-3">
            <h5 className="mb-0 fw-bold">📋 Attendance History</h5>
          </div>
          <div className="card-body px-4 pb-4">
            <div className="table-responsive">
              <table className="table table-hover table-bordered align-middle">
                <thead className="table-light">
                  <tr>
                    <th className="px-3">Date</th>
                    <th className="px-3">Check-In</th>
                    <th className="px-3">Check-Out</th>
                    <th className="px-3 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceHistory.length > 0 ? (
                    attendanceHistory.map((item) => {
                      const isToday =
                        new Date(item.date).toDateString() ===
                        new Date().toDateString();
                      return (
                        <tr
                          key={item._id}
                          className={isToday ? "table-warning" : ""}
                        >
                          <td className="px-3">{formatDate(item.date)}</td>
                          <td className="px-3">{formatTime(item.checkIn)}</td>
                          <td className="px-3">{formatTime(item.checkOut)}</td>
                          <td className="text-center">
                            {getStatusBadge(item)}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center py-4 text-muted">
                        No attendance records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
